import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightIcon from '@mui/icons-material/Light';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PDFReport from '../MapView/modals/PDFReport';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Full-screen modal that fetches inspection data and renders PDFReport for High Mast inspections.
 * Supports auto-download (generates PDF, uploads to S3, downloads to user) and batch mode.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - inspection: object from coverage-summary (id, asset_id, asset_name, asset_code, etc.)
 *  - autoDownload: boolean — auto-trigger PDF generation + save when ready
 *  - onReportGenerated: ({ success, error? }) => void — called after auto-download completes
 *  - batchProgress: { current, total } | null — if set, shows batch progress bar
 */
const HighMastReportPreviewModal = ({ open, onClose, inspection, autoDownload = false, onReportGenerated, batchProgress }) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inspectionData, setInspectionData] = useState(null);
  const [defectReport, setDefectReport] = useState({ loading: false, error: null, data: null });
  const [photos, setPhotos] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [assetDetails, setAssetDetails] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);

  // Auto-download state
  const [phase, setPhase] = useState('loading'); // loading | rendering | generating | uploading | done | error
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0, phase: '' });
  const downloadRef = useRef();
  const autoDownloadFiredRef = useRef(false);

  // Track the inspection ID we're currently processing to detect changes
  const currentInspectionIdRef = useRef(null);

  useEffect(() => {
    if (!open || !inspection?.id) return;

    // If the inspection ID changed, reset auto-download flag
    if (currentInspectionIdRef.current !== inspection.id) {
      currentInspectionIdRef.current = inspection.id;
      autoDownloadFiredRef.current = false;
    }

    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        setPhase('loading');

        // 1. Fetch full inspection details
        const inspectionResponse = await api.get(`/inspections/${inspection.id}`);
        const inspData = inspectionResponse.data;
        setInspectionData(inspData);

        // 2. Fetch asset details
        let assetData = null;
        if (inspData.asset_id) {
          try {
            const assetResponse = await api.get(`/assets/${inspData.asset_id}`);
            assetData = assetResponse.data;
            setAssetDetails(assetData);
          } catch (assetErr) {
            console.warn('Could not fetch asset details:', assetErr);
          }
        }

        // 3. Fetch photos
        const photosResponse = await api.get(`/inspections/${inspection.id}/photos`);
        const allPhotos = photosResponse.data || [];
        setPhotos(allPhotos);

        // 4. Fetch defects
        const defectsResponse = await api.get(`/inspections/${inspection.id}/defects`);
        const defects = defectsResponse.data || [];

        // 5. Fetch bounding boxes for each defect
        const photoMap = new Map(allPhotos.map(p => [p.photo_path, p]));

        const defectsWithBoundingBoxes = await Promise.all(
          defects.map(async (item) => {
            return api.get(`/defects/${item.id}/bounding-box`)
              .then(bboxResponse => ({ ...item, bounding_box: bboxResponse.data }))
              .catch(() => ({ ...item, bounding_box: null }));
          })
        );

        // 6. Enrich defects with photo data
        const photoPathsWithDefects = new Set();
        const enrichedDefects = defectsWithBoundingBoxes.map(defect => {
          if (defect.photo_path && photoMap.has(defect.photo_path)) {
            const photo = photoMap.get(defect.photo_path);
            photoPathsWithDefects.add(defect.photo_path);
            return {
              ...defect,
              original_image_path: photo.photo_path,
              image_width: photo.image_width,
              image_height: photo.image_height,
            };
          }
          return defect;
        });

        const photosWithDefects = allPhotos.filter(photo => photoPathsWithDefects.has(photo.photo_path));

        // 7. Calculate defect types
        const defectTypesMap = {};
        enrichedDefects.forEach(defect => {
          if (defect.defect_type_id && defect.defect_type_name) {
            if (!defectTypesMap[defect.defect_type_id]) {
              defectTypesMap[defect.defect_type_id] = {
                defect_type_id: defect.defect_type_id,
                defect_type_name: defect.defect_type_name,
                count: 0
              };
            }
            defectTypesMap[defect.defect_type_id].count++;
          }
        });
        const calculatedDefectTypes = Object.values(defectTypesMap);

        // 8. Set defect report data
        setDefectReport({
          loading: false,
          error: null,
          data: {
            total: enrichedDefects.length,
            defects: enrichedDefects,
            defectTypes: calculatedDefectTypes,
            photosWithDefects: photosWithDefects,
            photosWithDefectsCount: photosWithDefects.length,
          }
        });

        // 9. Fetch severity levels
        try {
          const severityResponse = await api.get('/severity-levels');
          setSeverityLevels(severityResponse.data || []);
        } catch (sevErr) {
          console.warn('Could not fetch severity levels:', sevErr);
        }

        // 10. Build report summary
        const inspectionDateStr = inspData.inspection_date || inspData.date;
        const inspectionDate = inspectionDateStr ? new Date(inspectionDateStr) : new Date();

        setReportSummary({
          network: { name: assetData?.network_name || 'N/A' },
          customField: {
            label: 'Asset ID',
            value: assetData?.code || 'N/A'
          },
          assetRating: assetData?.rating_name || 'Not Rated',
          inspectionType: inspData.inspection_type_name || 'Routine Aerial Inspection',
          inspectionCycle: inspData.cycle || null,
          inspectionConditionRating: inspData.condition_rating_name || 'Not Rated',
          startDate: inspectionDate,
          endDate: inspectionDate,
          selectAllDefects: true,
          selectedDefectTypes: calculatedDefectTypes,
          totalDefectTypes: calculatedDefectTypes.length,
          generatedBy: user?.fullName || user?.full_name || user?.username || 'System User'
        });

        setLoading(false);
        setPhase('rendering');
      } catch (err) {
        console.error('Error loading high mast report data:', err);
        setError(err.message || 'Failed to load report data');
        setLoading(false);
        setPhase('error');
        if (autoDownload && onReportGenerated) {
          onReportGenerated({ success: false, error: err.message, inspection });
        }
      }
    };

    fetchAllData();
  }, [open, inspection?.id]);

  // Auto-download: triggered by PDFReport's onReady callback
  const handlePdfReady = useCallback(async () => {
    if (!autoDownload || autoDownloadFiredRef.current) return;
    autoDownloadFiredRef.current = true;

    try {
      setPhase('generating');

      if (!downloadRef.current?.generatePDF) {
        throw new Error('PDF generator not ready');
      }

      const pdfData = await downloadRef.current.generatePDF(false);
      if (!pdfData) throw new Error('Failed to generate PDF');

      // Upload to backend
      setPhase('uploading');
      const reportName = `High Mast Inspection Report - ${inspection.asset_name} - ${new Date().toLocaleDateString()}`;
      const description = `High Mast inspection report for ${inspection.asset_name} (${inspection.asset_code}) containing ${defectReport?.data?.total || 0} defects`;

      const parameters = {
        inspectionId: inspection.id,
        assetType: 'High Mast',
        generatedAt: new Date().toISOString(),
        defectCount: defectReport?.data?.total || 0,
        photoCount: defectReport?.data?.photosWithDefectsCount || 0
      };

      const formData = new FormData();
      formData.append('pdfFile', pdfData.pdfBlob, pdfData.filename);
      formData.append('reportName', reportName);
      formData.append('reportType', 'inspection_report');
      formData.append('assetType', 'High Mast');
      formData.append('description', description);
      formData.append('parameters', JSON.stringify(parameters));
      if (inspectionData?.asset_id) {
        formData.append('assetId', inspectionData.asset_id);
      }
      if (inspectionData?.inspection_date) {
        const dateStr = new Date(inspectionData.inspection_date).toISOString().split('T')[0];
        formData.append('startDate', dateStr);
        formData.append('endDate', dateStr);
      }

      const response = await api.post('/reports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to save report');
      }

      // Trigger browser download
      const url = URL.createObjectURL(pdfData.pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setPhase('done');
      console.log('Report saved and downloaded:', response.data);

      if (onReportGenerated) {
        onReportGenerated({ success: true, inspection });
      }
    } catch (err) {
      console.error('Auto-download error:', err);
      setPhase('error');
      setError(err.message || 'Failed to generate/save report');
      if (onReportGenerated) {
        onReportGenerated({ success: false, error: err.message, inspection });
      }
    }
  }, [autoDownload, inspection, inspectionData, defectReport, onReportGenerated]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setLoading(true);
      setError(null);
      setInspectionData(null);
      setDefectReport({ loading: false, error: null, data: null });
      setPhotos([]);
      setSeverityLevels([]);
      setAssetDetails(null);
      setReportSummary(null);
      setPhase('loading');
      autoDownloadFiredRef.current = false;
      currentInspectionIdRef.current = null;
    }
  }, [open]);

  // Phase display text
  const getPhaseText = () => {
    switch (phase) {
      case 'loading': return 'Loading inspection data...';
      case 'rendering': return 'Rendering report pages...';
      case 'generating': return 'Generating PDF...';
      case 'uploading': return 'Saving report...';
      case 'done': return 'Report saved and downloaded!';
      case 'error': return 'Error occurred';
      default: return '';
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          py: 1.5,
          px: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <LightIcon sx={{ color: '#7c4dff' }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                High Mast Inspection Report
              </Typography>
              {phase === 'done' && <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />}
            </Box>
            {inspection && (
              <Typography variant="caption" color="text.secondary">
                {inspection.asset_name} ({inspection.asset_code})
              </Typography>
            )}

            {/* Auto-download progress */}
            {autoDownload && phase !== 'error' && (
              <Box sx={{ mt: 0.5 }}>
                {/* Batch progress */}
                {batchProgress && (
                  <Box sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" fontWeight={600} color="primary">
                        Overall: {batchProgress.current} of {batchProgress.total} reports
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round((batchProgress.current / batchProgress.total) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(batchProgress.current / batchProgress.total) * 100}
                      sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                    />
                  </Box>
                )}
                {/* Current phase */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {phase !== 'done' && <CircularProgress size={12} />}
                  <Typography variant="caption" color="text.secondary">
                    {getPhaseText()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary">
              Loading inspection data...
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            <Alert severity="error" sx={{ maxWidth: 500 }}>
              <Typography variant="h6" gutterBottom>Error</Typography>
              <Typography>{error}</Typography>
            </Alert>
          </Box>
        )}

        {!loading && !error && inspectionData && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <PDFReport
              inspection={inspectionData}
              defectReport={defectReport}
              photos={photos}
              severityLevels={severityLevels}
              assetType="High Mast"
              reportTitle="High Mast Inspection Report"
              reportSummary={reportSummary}
              reportDate={new Date()}
              assetDisplayImage={assetDetails?.display_image_path || null}
              hideDownloadButton={autoDownload}
              onDownloadRef={autoDownload ? downloadRef : undefined}
              onReady={autoDownload ? handlePdfReady : undefined}
              onLoadingChange={autoDownload ? (isGenerating) => {
                if (isGenerating) setPhase('generating');
              } : undefined}
              onProgressChange={autoDownload ? setPdfProgress : undefined}
              fullPage
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HighMastReportPreviewModal;
